import { createContext, useState, useEffect, useRef, useCallback } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import type { AuthContextType, User, SessionResponse } from '@/data/types/auth.types'
import type { Organization } from '@/data/types/organization.types'
import { fetchWithAuth } from '@/data/api/client'
import { supabase, isRecoveryPending, clearRecoveryFlag } from '@/lib/supabase'

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [isOnFreePlan, setIsOnFreePlan] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const checkAuthCalled = useRef(false)
  const authInProgress = useRef(false)
  const userRef = useRef<User | null>(null)
  
  // Keep userRef in sync with user state
  userRef.current = user

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut()
    } catch (error) {
      console.error('Sign out error:', error)
    }
    
    setUser(null)
    setOrganization(null)
    setIsOnFreePlan(false)
    toast.success('Logged out successfully')
    navigate('/auth/login')
    setLoading(false)
  }, [navigate])

  // Sign out without navigation or toast - for use cases like password reset
  // Clears state FIRST (synchronously) to prevent flash of authenticated content
  const signOutSilently = useCallback(async () => {
    setUser(null)
    setOrganization(null)
    setIsOnFreePlan(false)
    setLoading(false)
    
    try {
      await supabase.auth.signOut()
    } catch (error) {
      console.error('Silent sign out error:', error)
    }
  }, [])

  const checkAuth = useCallback(async (options?: { skipIfInProgress?: boolean }) => {
    // If auth is already in progress and we should skip, just ensure loading is false
    if (authInProgress.current) {
      if (options?.skipIfInProgress) {
        // Don't block - just return without changing loading state
        return
      }
      // Wait for the current auth check to complete instead of blocking
      // This prevents the loading state from getting stuck
      return
    }
    
    authInProgress.current = true

    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        setUser(null)
        setOrganization(null)
        setIsOnFreePlan(false)
        setLoading(false)
        authInProgress.current = false
        return
      }

      if (isRecoveryPending()) {
        clearRecoveryFlag()
        setUser(null)
        setOrganization(null)
        setIsOnFreePlan(false)
        setLoading(false)
        authInProgress.current = false
        navigate('/auth/reset-password')
        return
      }

      const sessionData: SessionResponse = await fetchWithAuth('/api/v1/auth/session')

      if (sessionData.requires_password_reset) {
        setUser(null)
        setOrganization(null)
        setIsOnFreePlan(false)
        setLoading(false)
        authInProgress.current = false
        navigate('/auth/reset-password')
        return
      }

      setUser(sessionData.user)
      setOrganization(sessionData.organization)
      setIsOnFreePlan(sessionData.is_on_free_plan ?? false)

      Promise.all([
        queryClient.prefetchQuery({
          queryKey: ['pricing', 'models'],
          queryFn: async () => {
            return fetchWithAuth('/api/v1/pricing-configurations/models')
          },
          staleTime: 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: ['metadata', 'keys'],
          queryFn: async () => {
            return fetchWithAuth('/api/v1/metadata-registry/keys')
          },
          staleTime: 60 * 1000,
        }),
      ]).catch((error) => {
        console.debug('Failed to prefetch models/metadata:', error)
      })
    } catch (error) {
      console.error('Auth check failed:', error)
      
      if (!(error instanceof TypeError && error.message.includes('fetch'))) {
        setUser(null)
        setOrganization(null)
        setIsOnFreePlan(false)
      }
    } finally {
      setLoading(false)
      authInProgress.current = false
    }
  }, [navigate, queryClient])

  const handleAcceptInvitation = useCallback(
    async (invitationToken: string) => {
      try {
        await fetchWithAuth('/api/v1/team/invitations/accept', {
          method: 'POST',
          body: JSON.stringify({ token: invitationToken }),
        })

        const newUrl = window.location.pathname
        window.history.replaceState({}, '', newUrl)

        toast.success('Invitation accepted successfully!', {
          description: 'Welcome to the team!',
        })

        return true
      } catch (error) {
        console.error('Failed to accept invitation:', error)
        toast.error('Failed to accept invitation. Please try again.')
        return false
      }
    },
    []
  )

  // Force logout listener
  useEffect(() => {
    const handleForceLogout = () => {
      logout()
      toast.error('Your session has expired. Please log in again.')
    }
    
    window.addEventListener('auth:logout', handleForceLogout)
    return () => window.removeEventListener('auth:logout', handleForceLogout)
  }, [logout])

  useEffect(() => {
    if (checkAuthCalled.current) return
    checkAuthCalled.current = true

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // TOKEN_REFRESHED happens when tab regains focus - don't re-authenticate
        // Just silently continue with the refreshed token
        if (event === 'TOKEN_REFRESHED') {
          // Token was refreshed successfully, no need to re-check auth
          // The user/organization state is already valid
          return
        }
        
        if (event === 'SIGNED_IN' && session?.access_token) {
          // Only show loading if we don't already have a user
          // This prevents the loading flash when tab regains focus
          const isReauthentication = userRef.current !== null
          if (!isReauthentication) {
            setLoading(true)
          }
          
          const hashParams = new URLSearchParams(window.location.hash.substring(1))
          const type = hashParams.get('type')
          
          if (type === 'signup' || type === 'recovery') {
            window.history.replaceState(null, '', window.location.pathname)
          }
          
          if (type === 'signup') {
            setLoading(false)
            return
          }
          
          const urlParams = new URLSearchParams(window.location.search)
          const invitationToken = urlParams.get('invitation_token') || hashParams.get('invitation_token')
          
          if (invitationToken) {
            try {
              await fetchWithAuth('/api/v1/auth/session')
            } catch {
              // Ignore errors - user might already exist
            }
            
            const success = await handleAcceptInvitation(invitationToken)
            if (!success) {
              setLoading(false)
              return
            }
          }
          
          // If already authenticated, skip re-checking (tab focus scenario)
          if (isReauthentication) {
            return
          }
          
          await checkAuth()
        } else if (event === 'SIGNED_OUT') {
          setUser(null)
          setOrganization(null)
          setIsOnFreePlan(false)
          setLoading(false)
        }
      }
    )

    const initAuth = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const type = hashParams.get('type')
      
      const urlParams = new URLSearchParams(window.location.search)
      const invitationToken = urlParams.get('invitation_token') || hashParams.get('invitation_token')
      
      if (invitationToken) {
        setLoading(true)
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.access_token) {
          try {
            await fetchWithAuth('/api/v1/auth/session')
          } catch {
            // Ignore errors - user might already exist
          }
          
          const success = await handleAcceptInvitation(invitationToken)
          if (!success) {
            setLoading(false)
            return
          }
          
          await checkAuth()
          return
        }
      }
      
      if (type === 'signup') {
        setLoading(false)
        return
      }
      
      await checkAuth()
    }

    initAuth()

    return () => {
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider value={{ user, organization, loading, isOnFreePlan, logout, checkAuth, signOutSilently }}>
      {children}
    </AuthContext.Provider>
  )
}

